import * as React from "react";
import { useSearchParams } from "react-router";

import { breakpoints } from "@/providers/theme/themes";
import {
  QuickSearch,
  QuickSearchButton,
  ResourceView,
  useQuickSearchHotkey,
  useResourceView,
  type Filter,
  type FilterDefinition,
  type FilterMatchMode,
  type QuickSearchProperty,
} from "@/shared/components/ResourceView";
import { type MetricRecord, TOPIC_STATS_PREFIX } from "@/shared/domain/metrics";
import { useMetricsBySession } from "@/shared/domain/metrics/hooks";
import { SearchParams } from "@/shared/environment";
import { Banner, Dialog } from "@/shared/foundation/primitives";
import {
  ColumnVisibility,
  DataTable,
  deriveLoadingState,
  useTreeRows,
} from "@/shared/foundation/primitives/Table";
import { ancestorPaths } from "@/shared/tree/pathTree";

import { useSessionOutletContext } from "../useSessionOutletContext";

import {
  COLUMN_PICKER_OPTIONS,
  DEFAULT_VISIBLE_COLUMN_IDS,
  METRIC_COLUMNS,
} from "./metricColumns";
import { MetricDetailCard } from "./MetricDetailCard";
import {
  METRIC_GROUP_SORT_KEYS,
  METRIC_SORT_KEYS,
  metricId,
  metricLeaves,
  metricPath,
  type MetricTreeRow,
} from "./metricLeaves";
import { MetricSearchContext } from "./metricSearchContext";
import { filterMetricRecords } from "./sessionMetricFilters";
import styles from "./SessionMetricsTab.module.css";

/** The table's page size: large enough that most sessions fit on one page. */
const ROWS_PER_PAGE = 100;

/** Rows are one line high, so they are shorter than the theme's two-line rows. */
const ROW_HEIGHT = 44;

const NO_FILTERS: readonly Filter[] = [];
const NO_RECORDS: readonly MetricRecord[] = [];

// A metric name has no ID shape to route on, so the search looks only in
// names, as on the Actions list.
const QUICK_SEARCH_PROPERTIES: QuickSearchProperty[] = [
  { field: "name", name: "Name", fallback: true },
];

/**
 * The text a Name "contains" filter looks for, which each row marks in its
 * name: the search's, or the same filter added from the Filter menu. Empty
 * when there is none.
 */
function nameSearchText(filters: readonly Filter[]): string {
  const search = filters.find(
    (filter) =>
      filter.type === "string" &&
      filter.field === "name" &&
      filter.comparator === "CONTAINS",
  );
  return search?.type === "string" ? (search.values[0] ?? "") : "";
}

/**
 * The windows the app's sidebar folds away on (`NavigationSidebarProvider`).
 * There a metric's details open in a dialog instead of beside the table: the
 * two switch together, so the card neither drops into a dialog while the
 * sidebar still takes room nor comes back the moment the sidebar folds.
 */
const SIDEBAR_FOLDED_QUERY = `(max-width: ${breakpoints.md}px)`;

/** Whether the window matches `query`, kept current as it resizes. */
function useMediaQuery(query: string): boolean {
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);
      list.addEventListener("change", onChange);
      return () => list.removeEventListener("change", onChange);
    },
    [query],
  );
  return React.useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
  );
}

/**
 * "Metrics" tab on the session-detail surface. Lists every metric attached
 * to the session named in the URL (`/sessions/:sessionId/metrics`) as a tree
 * table: one level per dotted name part, so `imu.accel.x` sits under `imu`
 * and then `accel`. Groups start closed. A group row shows its name and how
 * many records its metrics hold between them; a metric row its record count,
 * newest value and when it was published. Sorting a column orders each
 * group's metrics and keeps groups together; sorting Records orders the
 * groups by their records too.
 *
 * Find metrics, or Cmd/Ctrl-K, opens the quick search the other list pages
 * use. As there, what it applies is a Name "contains" filter that replaces
 * any filters already set, and each name marks the text it matched. The
 * session's records arrive in one request, so the search and the Filter
 * narrow them in memory. Narrowing opens every group that still holds a
 * match, and clearing it puts the groups back as they were. Pages
 * count the rows shown, groups included; a page that starts inside a group
 * repeats it, and the groups above it, at its top, marked "continued".
 *
 * Clicking a metric selects it and opens a `MetricDetailCard` beside the
 * table or, on a window too narrow for the app's sidebar, in a dialog centred
 * over the page. Esc or the card's close button closes it, as does a tap on the
 * dialog's backdrop, and focus that was in the card returns to the row.
 * The selection is held in the URL as the metric's name, so it survives a
 * reload and can be linked to; a link opens the page that holds the metric.
 *
 * A screen reader hears how many metrics a search or filter leaves, and that
 * details opened beside the table; the dialog is named by its metric.
 */
export function SessionMetricsTab() {
  const { session } = useSessionOutletContext();
  const sessionId = session.session_id;
  const orgId = session.org_id;

  // Only the metrics customers published. Of the topic stats, the Topics tab
  // shows message counts and seven field stats; a field's count, sum and
  // percentiles appear nowhere on the session page.
  const query = useMetricsBySession(sessionId, orgId, {
    excludeNamePrefix: TOPIC_STATS_PREFIX,
  });
  const records = query.data ?? NO_RECORDS;

  const view = useResourceView<MetricTreeRow>({
    getRowId: (row) => row.id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
    preferences: { rowsPerPage: ROWS_PER_PAGE },
  });
  const filters =
    view.filters.type === "structured" ? view.filters.filters : NO_FILTERS;
  const matchMode: FilterMatchMode =
    view.filters.type === "structured" ? view.filters.matchMode : "AND";

  const quickSearch = useQuickSearchHotkey();
  const searchText = nameSearchText(filters);

  const metricCount = React.useMemo(
    () => new Set(records.map((record) => record.name)).size,
    [records],
  );
  const filterDefinitions = React.useMemo(
    () => buildFilterDefinitions(records),
    [records],
  );

  // -- What the search and the Filter leave, as a tree -------------------------

  const leaves = React.useMemo(
    () => metricLeaves(filterMetricRecords(records, { filters, matchMode })),
    [records, filters, matchMode],
  );

  const isNarrowed = filters.length > 0;
  // Heard by a screen reader when a search or filter changes the rows.
  const matchAnnouncement = isNarrowed ? matchCountText(leaves.length) : "";
  const tree = useTreeRows({
    table: view.table,
    leaves,
    getPath: metricPath,
    getId: metricId,
    sortKeys: METRIC_SORT_KEYS,
    groupSortKeys: METRIC_GROUP_SORT_KEYS,
    openAll: isNarrowed,
  });
  const { reveal, toggle } = tree;

  // -- Selection (URL-driven) -------------------------------------------------

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedName = searchParams.get(SearchParams.SELECTED_METRIC);
  const selectedLeaf = React.useMemo(
    () =>
      selectedName
        ? leaves.find((leaf) => leaf.name === selectedName)
        : undefined,
    [leaves, selectedName],
  );

  const setSelection = React.useCallback(
    (name: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (name === null) {
            next.delete(SearchParams.SELECTED_METRIC);
          } else {
            next.set(SearchParams.SELECTED_METRIC, name);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Closing the details from inside them would drop focus along with the
  // card, and a tap on the dialog's backdrop has already dropped it, so either
  // way it moves to the metric's row first. Moved after the card is gone, it
  // would land on a row the table is about to redraw without its selected
  // mark.
  const { focusRow } = view.table;
  const detailsRef = React.useRef<HTMLDivElement>(null);
  const closeDetails = React.useCallback(() => {
    const focused = document.activeElement;
    const focusInDetails = detailsRef.current?.contains(focused) ?? false;
    const focusLost = focused === null || focused === document.body;
    if (selectedName !== null && (focusInDetails || focusLost)) {
      focusRow(selectedName);
    }
    setSelection(null);
  }, [selectedName, setSelection, focusRow]);

  // Beside the table while the window has room for the sidebar; otherwise in
  // a dialog.
  const isNarrow = useMediaQuery(SIDEBAR_FOLDED_QUERY);
  const detailsBeside = selectedLeaf !== undefined && !isNarrow;
  // Details beside the table open out of a screen reader's sight, so they are
  // announced; a dialog announces itself.
  const detailsAnnouncement = detailsBeside
    ? `Showing details for ${selectedLeaf.name}`
    : "";
  // The dialog is named by the card's heading, and reading starts there.
  const detailsHeadingId = React.useId();

  // Bring a deep-linked selection into view: its groups open, on its page.
  React.useEffect(() => {
    if (selectedName) {
      reveal(selectedName);
    }
  }, [selectedName, reveal]);

  // Esc closes the details beside the table, unless something on top of the
  // page (a menu, a dialog) took the key first. The dialog handles its own.
  React.useEffect(() => {
    if (!detailsBeside) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented) {
        closeDetails();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [detailsBeside, closeDetails]);

  // -- Rows ---------------------------------------------------------------------

  const handleRowClick = React.useCallback(
    (row: MetricTreeRow) => {
      if (row.kind === "leaf") {
        setSelection(row.id === selectedName ? null : row.id);
      }
    },
    [setSelection, selectedName],
  );

  const treeConfig = React.useMemo(
    () => ({
      getPlacement: (row: MetricTreeRow) => row,
      onToggle: (row: MetricTreeRow) => {
        if (row.kind === "group") {
          toggle(row);
        }
      },
      hasGroups: tree.hasGroups,
    }),
    [toggle, tree.hasGroups],
  );

  // -- Render -------------------------------------------------------------------

  if (query.error) {
    return (
      <Banner variant="error">
        Failed to load metrics for this session: {query.error.message}
      </Banner>
    );
  }

  const hasMetrics = metricCount > 0;

  return (
    <ResourceView.Root>
      {hasMetrics && (
        <>
          <QuickSearch
            open={quickSearch.isOpen}
            onClose={quickSearch.close}
            properties={QUICK_SEARCH_PROPERTIES}
            placeholder="Search by name…"
            onSubmit={(filter) => {
              view.clearFilters();
              view.commit({ type: "structured", filter });
            }}
          />
          <ResourceView.Actions>
            <QuickSearchButton
              onClick={quickSearch.open}
              label="Find metrics"
            />
            <ResourceView.Filters
              filterDefinitions={filterDefinitions}
              filters={view.filters}
              onFilterSelected={view.commit}
            />
            <ColumnVisibility
              options={COLUMN_PICKER_OPTIONS}
              visibleColumnIds={view.table.visibleColumnIds}
              setColumnVisibility={view.table.setColumnVisibility}
            />
          </ResourceView.Actions>
          <ResourceView.ActiveFilters
            filters={view.filters}
            filterDefinitions={filterDefinitions}
            onRemove={view.removeFilter}
            onUpdate={view.updateFilter}
            onClear={view.clearFilters}
            onMatchModeChange={view.setMatchMode}
            onFilterSelected={view.commit}
          />
        </>
      )}
      <div
        className={styles.layout}
        data-with-details={detailsBeside ? "" : undefined}
      >
        <div className={styles.tableArea}>
          <MetricSearchContext.Provider value={searchText}>
            <DataTable<MetricTreeRow>
              table={view.table}
              ariaLabel="Session metrics"
              columns={METRIC_COLUMNS}
              data={tree.visibleRows}
              totalRowCount={tree.totalRowCount}
              hasNextPage={tree.hasNextPage}
              loading={deriveLoadingState(query)}
              tree={treeConfig}
              rowHeight={ROW_HEIGHT}
              onRowClick={handleRowClick}
              selectedRowId={selectedLeaf?.name}
              noRowsContent={
                hasMetrics
                  ? "No metrics match the current filters."
                  : "No metrics have been published to this session yet."
              }
            />
          </MetricSearchContext.Provider>
        </div>
        {selectedLeaf && !isNarrow && (
          <div ref={detailsRef} className={styles.detailPane}>
            <MetricDetailCard
              name={selectedLeaf.name}
              records={selectedLeaf.records}
              orgId={orgId}
              onClose={closeDetails}
            />
          </div>
        )}
      </div>
      {selectedLeaf && isNarrow && (
        <Dialog
          open
          onOpenChange={(open) => {
            if (!open) {
              closeDetails();
            }
          }}
        >
          <Dialog.Content
            ref={detailsRef}
            aria-labelledby={detailsHeadingId}
            initialFocus={() => document.getElementById(detailsHeadingId)}
            // `closeDetails` puts focus back on the metric's row.
            finalFocus={false}
            className={styles.detailDialog}
          >
            <MetricDetailCard
              name={selectedLeaf.name}
              records={selectedLeaf.records}
              orgId={orgId}
              onClose={closeDetails}
              className={styles.dialogCard}
              dialogHeadingId={detailsHeadingId}
            />
          </Dialog.Content>
        </Dialog>
      )}
      <div role="status" className="sr-only">
        {matchAnnouncement}
      </div>
      <div role="status" className="sr-only">
        {detailsAnnouncement}
      </div>
    </ResourceView.Root>
  );
}

/** How many metrics a search or filter leaves, as a screen reader hears it. */
function matchCountText(count: number): string {
  if (count === 0) {
    return "No metrics match";
  }
  return count === 1 ? "1 metric matches" : `${count} metrics match`;
}

/**
 * The Filter's fields for this session. Group shows only when some metric
 * name has a dot, and Invocation and Device only when some record has one:
 * a field with nothing to pick is left out.
 */
function buildFilterDefinitions(
  records: readonly MetricRecord[],
): FilterDefinition[] {
  const names = new Set<string>();
  const groups = new Set<string>();
  const invocations = new Set<string>();
  const devices = new Set<string>();
  for (const record of records) {
    names.add(record.name);
    for (const group of ancestorPaths(record.name)) {
      groups.add(group);
    }
    if (record.invocation_id) {
      invocations.add(record.invocation_id);
    }
    if (record.device_id) {
      devices.add(record.device_id);
    }
  }
  const sorted = (values: Set<string>) =>
    [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );

  const definitions: FilterDefinition[] = [
    {
      type: "string",
      field: "name",
      name: "Name",
      description: "The metric's full dotted name",
      allowedComparators: [
        "CONTAINS",
        "NOT_CONTAINS",
        "EQUALS",
        "NOT_EQUALS",
        "LIKE",
        "NOT_LIKE",
        "ANY_OF",
        "NONE_OF",
      ],
      suggestions: { kind: "list", values: sorted(names) },
    },
  ];
  if (groups.size > 0) {
    definitions.push({
      type: "string",
      field: "group",
      name: "Group",
      description: "A leading part of the name: imu.accel holds imu.accel.x",
      allowedComparators: ["EQUALS", "NOT_EQUALS", "ANY_OF", "NONE_OF"],
      suggestions: { kind: "list", values: sorted(groups) },
    });
  }
  if (invocations.size > 0) {
    definitions.push({
      type: "enum",
      field: "invocation_id",
      name: "Invocation",
      options: sorted(invocations),
    });
  }
  if (devices.size > 0) {
    definitions.push({
      type: "enum",
      field: "device_id",
      name: "Device",
      options: sorted(devices),
    });
  }
  definitions.push({
    type: "date",
    field: "published",
    name: "Last published",
  });
  return definitions;
}
